import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
	"inline-flex items-center justify-center gap-2 whitespace-nowrap text-xs font-medium uppercase tracking-wider transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-3 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-primary/70 focus-visible:ring-primary/30 focus-visible:ring-[2px] aria-invalid:ring-red-400/20 dark:aria-invalid:ring-red-400/40 aria-invalid:border-red-400 cursor-pointer font-mono",
	{
		variants: {
			variant: {
				default:
					"bg-primary/10 border border-primary/50 text-primary hover:bg-primary/20 hover:border-primary",
				destructive:
					"bg-red-500/10 border border-red-400/50 text-red-400 hover:bg-red-500/20 hover:border-red-500 focus-visible:ring-red-400/30",
				outline:
					"border border-border bg-transparent hover:bg-accent hover:text-accent-foreground hover:border-primary/50",
				secondary:
					"bg-secondary border border-secondary-foreground/20 text-secondary-foreground hover:bg-secondary/80",
				ghost:
					"text-muted-foreground hover:text-accent-foreground hover:bg-accent",
				link: "text-primary hover:text-primary/80 underline-offset-4 hover:underline",
			},
			size: {
				default: "h-9 px-4 py-2 has-[>svg]:px-3",
				sm: "h-8 gap-1.5 px-3 has-[>svg]:px-2.5",
				lg: "h-10 px-6 has-[>svg]:px-4",
				icon: "size-9",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
);

function Button({
	className,
	variant,
	size,
	asChild = false,
	...props
}: React.ComponentProps<"button"> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean;
	}) {
	const Comp = asChild ? Slot : "button";

	return (
		<Comp
			data-slot="button"
			className={cn(buttonVariants({ variant, size, className }))}
			{...props}
		/>
	);
}

export { Button, buttonVariants };
